/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../features/Pagination/Pagination';
import { useTemplate } from '../../../features/template/TemplateProvider';
import { OptionButton } from '../../../utils/Button';
import { TableSortArrow } from '../../../utils/IconComp';
import InstOption from '../../../utils/InstOption';
import { GetDate, GetTime } from '../../../utils/Show';
import { mkRelativeUrl } from '../../../utils/URL';
import cn from '../../../utils/cn';
import { handleDisable2faOpe } from '../CustomerOperation';
import { useCustomer } from '../CustomerProvider';

function TableRow({ item }) {
    const navigate = useNavigate();
    const [showMenu, setShowMenu] = useState(false);
    const {
        handleCustomerTypeModal,
        handleCustomerStatusModal,
        handleChangeCurrencyModal,
        handleShowEditModal,
        handleReload
    } = useCustomer();
    const { setMessage, setConfirm } = useTemplate();

    const handleShowInstMenu = (e) => {
        e.stopPropagation();
        setShowMenu((d) => !d);
    };

    const handleDisable2FA = (e) => {
        e.stopPropagation();
        setConfirm({
            text: 'Disabling Two-Factor Authentication will remove an additional layer of security on this account. Are you sure you want to proceed?',
            textAction: 'Yes',
            clickAction: () => {
                handleDisable2faOpe(item?.id)
                    .then(({ title }) => {
                        setMessage({ type: 'success', text: title });
                        setConfirm(null);
                        handleReload();
                    })
                    .catch((err) => {
                        setMessage({ type: 'error', text: err.toString() });
                    });
            }
        });
    };

    const isActive = item?.status === 'active';

    return (
        <tr className={cn(`group`, { 'placeholder-mode': item?.demo })}>
            <td>
                <span>{item?.id}</span>
            </td>
            <td>
                <span className="capitalize text-ellipsis overflow-hidden block">{item?.role}</span>
            </td>
            <td
                onClick={() => navigate(mkRelativeUrl(`/customers/${item?.id}/info`))}
                className="cursor-pointer group-hover:text-secondary group/row"
            >
                <div className="inline-flex items-center gap-1.5">
                    <span className="text-ellipsis overflow-hidden w-full block first-letter:!uppercase group-hover:font-medium group-hover/row:underline">
                        {item?.organization || item?.fullName || '-----'}
                    </span>
                    {item?.isNew && (
                        <span className="text-xs bg-secondary-200 text-secondary px-[7px] py-0.5 rounded no-underline">
                            New
                        </span>
                    )}
                </div>
            </td>
            <td>
                <span className="text-ellipsis overflow-hidden w-full block">
                    {item?.primaryEmail || '------'}
                </span>
            </td>
            <td className="hidden xl:table-cell">
                <span>
                    <div className="flex flex-row relative customer_apps h-8">
                        {item?.subscriptions?.map((app, index) => (
                            <img
                                key={app.id}
                                data-tooltip-id="myToolTip"
                                data-tooltip-content={app.subscriptionName}
                                src={app.logoUrl}
                                alt={app.name}
                                onClick={() =>
                                    navigate(mkRelativeUrl(`/subscriptions/manage/${app?.id}/info`))
                                }
                                className={`w-8 absolute top-1/2 -translate-y-1/2 rounded-full border cursor-pointer bg-white border-primary-200 app-${
                                    index + 1
                                }`}
                            />
                        ))}

                        {item?.subscriptions.length >= 5 && (
                            <span
                                data-tooltip-id="myToolTip"
                                data-tooltip-content={`${item.subscriptions.length - 3} more`}
                                className="flex flex-row items-center justify-center w-8 h-8 text-white absolute top-1/2 text-sm font-medium -translate-y-1/2 left-18 rounded-full border border-white bg-[#1B1B1F]/80 cursor-pointer z-10"
                            >
                                +{item.subscriptions.length - 3}
                            </span>
                        )}
                    </div>
                </span>
            </td>
            <td>
                <span className={item?.timeCreated ? '' : 'w-3/5 inline-block text-center'}>
                    {GetDate(item?.timeCreated) || '--'} <br />
                    <small>{GetTime(item?.timeCreated)}</small>
                </span>
            </td>
            <td className="text-center capitalize">
                <span className="text-center capitalize">{item?.currency || '--'}</span>
            </td>
            <td className="text-center capitalize">
                <span className={item?.tfaEnabled ? 'text-secondary' : 'text-error'}>
                    {item?.tfaEnabled ? 'Enabled' : 'Disabled'}
                </span>
            </td>
            <td className="text-center capitalize">
                <span className={isActive ? 'text-secondary' : 'text-warning'}>{item?.status}</span>
            </td>
            <td className="text-center !pr-5">
                <div className="flex flex-row w-full justify-end relative">
                    <OptionButton onClick={handleShowInstMenu} />

                    <InstOption showMenu={showMenu} setShowMenu={setShowMenu}>
                        <button
                            type="button"
                            onClick={() => navigate(mkRelativeUrl(`/customers/${item?.id}/info`))}
                        >
                            Manage
                        </button>
                        {isActive && (
                            <button type="button" onClick={(ev) => handleShowEditModal(ev, item)}>
                                Edit Info
                            </button>
                        )}

                        {isActive && (
                            <button
                                type="button"
                                onClick={(ev) => handleChangeCurrencyModal(ev, item)}
                            >
                                Change Currency
                            </button>
                        )}
                        {isActive && (
                            <button
                                type="button"
                                onClick={(ev) => handleCustomerTypeModal(ev, item)}
                            >
                                Change Role
                            </button>
                        )}
                        <button type="button" onClick={(ev) => handleCustomerStatusModal(ev, item)}>
                            {item?.status === 'active' ? 'Make Inactive' : 'Make Active'}
                        </button>

                        {item?.tfaEnabled && isActive && (
                            <button type="button" onClick={handleDisable2FA}>
                                Disable 2FA
                            </button>
                        )}
                    </InstOption>
                </div>
            </td>
        </tr>
    );
}

function TableHeader({ handleSortingData, sortedColumn, sortBy }) {
    return (
        <thead>
            <tr>
                <th onClick={() => handleSortingData('id')} scope="col">
                    <span className="uppercase">Id</span>
                    <TableSortArrow name="id" sortedColumn={sortedColumn} sortBy={sortBy} />
                </th>
                <th onClick={() => handleSortingData('role')} scope="col" className="w-24">
                    <span>Role</span>
                    <TableSortArrow name="role" sortedColumn={sortedColumn} sortBy={sortBy} />
                </th>
                <th onClick={() => handleSortingData('customer')} scope="col">
                    <span>Customer</span>
                    <TableSortArrow name="customer" sortedColumn={sortedColumn} sortBy={sortBy} />
                </th>
                <th scope="col" className="!cursor-default">
                    <span>Email</span>
                </th>
                <th scope="col" className="w-40 !cursor-default hidden xl:table-cell">
                    <span>Application</span>
                </th>
                <th onClick={() => handleSortingData('created')} scope="col" className="w-32 !px-1">
                    <span>Date Created</span>
                    <TableSortArrow name="created" sortedColumn={sortedColumn} sortBy={sortBy} />
                </th>
                <th
                    onClick={() => handleSortingData('currency')}
                    scope="col"
                    className="w-28 text-center"
                >
                    <span>Currency</span>
                    <TableSortArrow name="currency" sortedColumn={sortedColumn} sortBy={sortBy} />
                </th>
                <th scope="col" className="w-30 text-center !cursor-default">
                    <span>2FA</span>
                </th>
                <th
                    onClick={() => handleSortingData('status')}
                    scope="col"
                    className="text-center w-20 !px-0"
                >
                    <span>Status</span>
                    <TableSortArrow name="status" sortedColumn={sortedColumn} sortBy={sortBy} />
                </th>
                <th scope="col">#</th>
            </tr>
        </thead>
    );
}

function PageTable({ data, pagination, onChangePage, handleSortingData, sortedColumn, sortBy }) {
    return (
        <div className="customer_page_table mb-52">
            <table className="bik-table" cellPadding={0} cellSpacing={0}>
                <TableHeader
                    handleSortingData={handleSortingData}
                    sortedColumn={sortedColumn}
                    sortBy={sortBy}
                />
                <tbody className="">
                    {data && data?.customers?.length === 0 && (
                        <tr className="border-b border-[#1B1B1F]/10 h-12.5">
                            <td colSpan={10} className="!text-left">
                                <span className="text-primary-700 ">No Customers Found!</span>
                            </td>
                        </tr>
                    )}
                    {data?.customers?.map((item) => (
                        <TableRow key={item?.id} item={item} />
                    ))}
                </tbody>
            </table>
            <Pagination data={pagination} onChangePage={onChangePage} />
        </div>
    );
}

export default PageTable;
